/* Danone × UniBuy™ — Coupa cXML PunchOut Enablement screen.
 *
 * Admin-only handoff page: everything Danone's Coupa Buyer-Setup /
 * System Admin needs to configure the PunchOut Supplier Item — copy-to-
 * clipboard per value, printable, with the downloadable Excel workbook.
 *
 * Zero backend dependency: URLs are derived from window.location.origin,
 * so the same build shows the correct endpoint on UAT and production.
 * The SharedSecret is intentionally a placeholder — it is issued 1:1
 * over an encrypted channel and NEVER rendered in the UI.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Check,
  Download,
  ShieldCheck,
  Globe,
  ArrowLeftRight,
  ListChecks,
  HelpCircle,
  Lock,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

function CopyBtn({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (non-secure context) — user copies manually
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testid}
      title="Copy to clipboard"
      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:text-slate-900 hover:border-slate-400 transition-colors shrink-0"
    >
      {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
    </button>
  );
}

function Row({ label, value, note, secret = false, testid }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-1 md:gap-4 px-4 py-3 border-b border-slate-100 last:border-b-0">
      <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 pt-1">{label}</div>
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <code
            className={`text-[12px] font-mono break-all ${secret ? "text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" : "text-slate-900"}`}
            data-testid={testid}
          >
            {value}
          </code>
          {!secret && <CopyBtn value={value} testid={testid ? `${testid}-copy` : undefined} />}
        </div>
        {note && <div className="mt-1 text-[11px] text-slate-500 leading-snug">{note}</div>}
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <section className="rounded-xl bg-white border border-slate-200 overflow-hidden" data-testid={testid}>
      <header className="px-4 py-3 border-b border-slate-100 flex items-center gap-2.5 bg-slate-50/60">
        <span className="h-8 w-8 rounded-md bg-[#0072CE]/10 border border-[#0072CE]/20 text-[#0072CE] flex items-center justify-center shrink-0">
          <Icon size={15} />
        </span>
        <div>
          <h2 className="font-display font-semibold text-slate-900 text-sm tracking-tight">{title}</h2>
          {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

const STEPS = [
  ["Create Supplier Item", "Coupa → Setup → Suppliers → Create → “Infosys UniBuy™ (OMNISupply)”. Category: Indirect · MRO · Lab · PPE."],
  ["Enable PunchOut", "On the supplier record tick “PunchOut enabled” and select cXML 1.2.043 (Level 2, buyer → supplier)."],
  ["Enter the POSR URL", "Paste the PunchOutSetupRequest URL from the Connection card. Coupa POSTs the POSR cXML here on every buyer launch."],
  ["Enter credentials", "From/Sender Identity: DANONE.COUPA.NL (NetworkID) · To Identity: INFOSHOP-OMNISUPPLY (DUNS) · SharedSecret issued 1:1 by Infosys — never email it."],
  ["Attach extrinsics", "UserEmail (per-buyer), CountryCode, Currency, CompanyName=DANONE — these drive UniBuy™ pricing, tax and locale."],
  ["Confirm BrowserFormPost receiver", "Verify Danone's tenant return URL (typically https://danone.coupahost.com/punchout/order/receive). UniBuy™ POSTs the completed POOM here as cxml-urlencoded."],
  ["Entitle buyer groups", "Assign the supplier to the Coupa buyer groups that should see UniBuy™ (typically MRO · Lab · PPE · Consumables)."],
  ["UAT round-trip", "On the staging URL: launch as a test buyer, add ≥ 2 items, hit “Coupa cXML Cart Transfer”, confirm the requisition lands in Coupa with line-level mfg_pn / UNSPSC / tax / totals."],
  ["Cut over to production", "Swap the POSR URL to production, re-issue the SharedSecret, repeat the smoke test with one real buyer."],
  ["Monitor", "Infosys OMNISupply watches the PunchOut Session Inspector 24×7 — every POSR/POOM is logged with payload + delivery status."],
];

const FAQS = [
  ["Does Danone see “Aerchain” anywhere?", "No. This is a plain Coupa cXML PunchOut to Infosys UniBuy™. Every buyer-facing label says “Coupa cXML Cart Transfer”; Aerchain operates invisibly on the Infosys side."],
  ["Mixed-currency carts?", "UniBuy™ splits the cart into one POOM per (currency × tax-class) bucket — Coupa never receives a mixed-currency requisition. Buyers see the split preview before transferring."],
  ["Tax handling", "NL VAT 21% computed on hosted lines; sub-supplier lines carry the partner's own tax when quoted. Danone's tax-exemption certificates are matched automatically by country + UNSPSC segment."],
  ["Audit trail", "Every POSR, cart mutation and POOM delivery is persisted with timestamp, buyer identity and full cXML payload — available to Danone Audit on request."],
  ["SharedSecret rotation", "12-month cadence or on-demand. UniBuy™ supports two active secrets during rotation so Coupa can be updated with zero downtime."],
];

export default function PunchOutEnablementPage() {
  const auth = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const posrUrl = `${origin}/api/punchout/setup-request`;

  if (auth?.user && auth.user.role !== "admin") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="rounded-xl bg-white border border-slate-200 p-8 max-w-md text-center" data-testid="enablement-access-denied">
          <Lock size={22} className="mx-auto text-slate-400" />
          <h1 className="mt-3 font-display font-semibold text-slate-900">Admin only</h1>
          <p className="mt-1 text-sm text-slate-500">This handoff screen is restricted to Infosys BPM administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="punchout-enablement-page">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-4">
          <Link to="/infoshop" className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="enablement-back-link">
            <ArrowLeft size={16} />
            <span className="text-sm font-semibold">Back</span>
          </Link>
          <div className="flex items-center gap-3 min-w-0">
            <img src="/unibuy-wordmark.png" alt="UniBuy™" className="h-8 w-auto max-w-[130px] sm:max-w-none object-contain object-left" data-testid="enablement-logo" />
            <span className="hidden sm:inline-block h-6 w-px bg-slate-300" />
            <div className="leading-tight hidden sm:block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-bold">Coupa cXML PunchOut</div>
              <div className="font-display font-bold text-slate-900 text-sm">Danone Enablement</div>
            </div>
          </div>
          <a
            href="/UniBuy_PunchOut_Configuration_Danone_Coupa.xlsx"
            download
            data-testid="enablement-download-xlsx"
            className="ml-auto inline-flex items-center gap-2 rounded-md bg-[#0072CE] hover:bg-[#005BA6] text-white text-xs font-semibold px-3.5 py-2 shadow-sm transition-colors"
          >
            <Download size={13} /> Download Excel workbook
          </a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 lg:px-6 py-8 space-y-6">
        <div className="rounded-xl border border-[#0072CE]/25 bg-[#0072CE]/5 px-4 py-3 text-[13px] text-slate-700 leading-relaxed" data-testid="enablement-intro">
          Hand this screen (or the Excel workbook) to Danone's <span className="font-semibold">Coupa Buyer-Setup / System Admin</span>.
          Every value has a copy button. URLs are rendered for <span className="font-semibold">this environment</span> — open this page on production to copy production URLs.
        </div>

        <Card icon={Globe} title="Connection" subtitle="How Coupa reaches UniBuy™" testid="enablement-card-connection">
          <Row label="Protocol" value="cXML 1.2.043 · PunchOut Level 2" note="Standard Coupa buyer → supplier PunchOut." testid="enablement-protocol" />
          <Row label="PunchOutSetupRequest URL" value={posrUrl} note="Coupa POSTs the POSR cXML here. UniBuy™ replies with a PunchOutSetupResponse containing the StartPage URL." testid="enablement-posr-url" />
          <Row label="HTTP method" value="POST" testid="enablement-method" />
          <Row label="Content-Type" value="application/xml; charset=UTF-8" note="Also accepts application/x-www-form-urlencoded with a cxml-urlencoded field." testid="enablement-content-type" />
          <Row label="SLA" value="POSR < 2s p95 · POOM < 5s p95" testid="enablement-sla" />
        </Card>

        <Card icon={ShieldCheck} title="cXML credentials" subtitle="What goes into Coupa's PunchOut supplier config" testid="enablement-card-credentials">
          <Row label="From › Domain" value="NetworkID" testid="enablement-from-domain" />
          <Row label="From › Identity" value="DANONE.COUPA.NL" note="Danone's sender identity. Use DANONE.COUPA.<CountryCode> for other legal entities (…US, …BR)." testid="enablement-from-identity" />
          <Row label="To › Domain" value="DUNS" testid="enablement-to-domain" />
          <Row label="To › Identity" value="INFOSHOP-OMNISUPPLY" note="UniBuy™ wire identity. Do not change." testid="enablement-to-identity" />
          <Row label="Sender › Domain" value="NetworkID" testid="enablement-sender-domain" />
          <Row label="Sender › Identity" value="DANONE.COUPA.NL" testid="enablement-sender-identity" />
          <Row label="SharedSecret" value="Issued 1:1 by Infosys over an encrypted channel — never rendered here, never emailed" secret testid="enablement-shared-secret" />
          <Row label="Extrinsics" value="UserEmail · CountryCode=NL · Currency=EUR · CompanyName=DANONE" note="Drive UniBuy™ pricing, tax mode (VAT 21%) and locale." testid="enablement-extrinsics" />
        </Card>

        <Card icon={ArrowLeftRight} title="Return path — cart back to Coupa" subtitle="What happens when the buyer hits “Coupa cXML Cart Transfer”" testid="enablement-card-return">
          <Row label="BrowserFormPost URL (Coupa)" value="https://danone.coupahost.com/punchout/order/receive" note="Confirm the exact tenant path with Danone's Coupa admin before go-live." testid="enablement-bfp-url" />
          <Row label="Form field" value="cxml-urlencoded" note="UniBuy™ POSTs application/x-www-form-urlencoded with a single URL-encoded cXML field — Coupa's default contract." testid="enablement-bfp-field" />
          <Row label="Payload" value="PunchOutOrderMessage (cXML)" note="One POOM per (currency × tax-class) bucket — Coupa never receives a mixed-currency requisition." testid="enablement-payload" />
          <Row label="Buyer experience" value="No UniBuy™ login · Coupa-branded handoff" note="Buyers land directly in the catalog from Coupa and return automatically. Danone never sees Aerchain anywhere in the flow." testid="enablement-buyer-exp" />
        </Card>

        <Card icon={ListChecks} title="Coupa setup — 10 steps" subtitle="Buyer-Setup runbook" testid="enablement-card-steps">
          <ol className="divide-y divide-slate-100">
            {STEPS.map(([title, body], i) => (
              <li key={i} className="px-4 py-3 flex gap-3" data-testid={`enablement-step-${i + 1}`}>
                <span className="h-6 w-6 rounded-full bg-[#0072CE]/10 border border-[#0072CE]/25 text-[#0072CE] text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div>
                  <div className="text-sm font-semibold text-slate-900">{title}</div>
                  <div className="text-[12px] text-slate-600 leading-snug mt-0.5">{body}</div>
                </div>
              </li>
            ))}
          </ol>
        </Card>

        <Card icon={HelpCircle} title="FAQ" subtitle="What Danone's team usually asks" testid="enablement-card-faq">
          <dl className="divide-y divide-slate-100">
            {FAQS.map(([q, a], i) => (
              <div key={i} className="px-4 py-3" data-testid={`enablement-faq-${i + 1}`}>
                <dt className="text-sm font-semibold text-slate-900">{q}</dt>
                <dd className="text-[12px] text-slate-600 leading-snug mt-0.5">{a}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <footer className="text-[11px] text-slate-400 pb-4">
          Support: <span className="font-mono">unibuy-punchout-support@infosys.com</span> · 24×7 OMNISupply on-call rotation · UniBuy™ by Infosys BPM
        </footer>
      </main>
    </div>
  );
}
